/**
 * Oferta de especialidades del policlínico: qué se atiende, a qué precio y
 * con qué profesionales. Fuente única de esos tres datos.
 */
import { apiFetch } from "./client";
import type { TipoColegio } from "./profesionales";

export interface ProfesionalDeEspecialidad {
  id: number;
  nombre_completo: string;
  tipo_colegio: TipoColegio;
  activo: boolean;
  principal: boolean;
}

export interface EspecialidadPropiaCreate {
  nombre: string;
  codigo: string;
  /** Colegios que pueden ejercerla. Obligatorio: sin ellos nadie la asigna. */
  profesiones: string[];
  precio?: number | null;
}

export interface ServicioMini {
  id: number;
  codigo: string;
  nombre: string;
  precio: string;
  /** El servicio por defecto de la especialidad. Solo puede haber uno. */
  es_consulta: boolean;
  activo: boolean;
}

export interface ServicioCreate {
  especialidad_id: number;
  nombre: string;
  codigo: string;
  precio: number;
  es_consulta?: boolean;
}

export interface ServicioUpdate {
  nombre?: string;
  precio?: number;
  es_consulta?: boolean;
  activo?: boolean;
}

export interface OfertaEspecialidad {
  especialidad_id: number;
  codigo: string;
  nombre: string;
  /** Colegios que pueden ejercerla. Vacío = sin restricción declarada. */
  profesiones: TipoColegio[];
  activa: boolean;
  /**
   * Si en esta especialidad se toman funciones vitales antes de la consulta.
   *
   * Apagarlo no prohíbe tomarlas: deja de ser obligatorio para entrar al
   * consultorio y de aparecer en la bandeja de enfermería. A quien viene a que
   * le arreglen una muela no se le toma la presión para eso.
   */
  requiere_triaje: boolean;
  /** Decimal serializado como string. null = sin tarifa definida. */
  precio: string | null;
  profesionales: ProfesionalDeEspecialidad[];
  /** Lo que se presta y se cobra. `precio` es el del servicio marcado consulta. */
  servicios: ServicioMini[];
  /** Citas futuras sin atender. > 0 bloquea dejar de ofrecerla. */
  citas_vigentes: number;
  /** null = del catálogo de la plataforma; con valor = propia de este policlínico. */
}

const BASE = "/v1/mi-policlinico/especialidades";

/**
 * Crea una especialidad propia de este policlínico.
 *
 * No entra al catálogo global de la plataforma: solo la ve este policlínico.
 * Nace ya ofrecida, porque quien la crea es porque la atiende.
 */
export function crearEspecialidadPropia(
  payload: EspecialidadPropiaCreate,
): Promise<OfertaEspecialidad> {
  return apiFetch<OfertaEspecialidad>(BASE, { method: "POST", body: payload });
}

export function listOferta(): Promise<OfertaEspecialidad[]> {
  return apiFetch<OfertaEspecialidad[]>(BASE);
}

/** PATCH parcial: omitir un campo lo deja intacto; precio null borra la tarifa. */
export function updateOferta(
  especialidadId: number,
  cambios: { activa?: boolean; precio?: number | null; requiere_triaje?: boolean },
): Promise<OfertaEspecialidad> {
  return apiFetch<OfertaEspecialidad>(`${BASE}/${especialidadId}`, {
    method: "PATCH",
    body: cambios,
  });
}

export function asignarProfesional(
  especialidadId: number,
  profesionalId: number,
): Promise<OfertaEspecialidad> {
  return apiFetch<OfertaEspecialidad>(`${BASE}/${especialidadId}/profesionales`, {
    method: "POST",
    body: { profesional_id: profesionalId },
  });
}

export function quitarProfesional(
  especialidadId: number,
  profesionalId: number,
): Promise<OfertaEspecialidad> {
  return apiFetch<OfertaEspecialidad>(
    `${BASE}/${especialidadId}/profesionales/${profesionalId}`,
    { method: "DELETE" },
  );
}

export function crearServicio(payload: ServicioCreate): Promise<OfertaEspecialidad> {
  return apiFetch<OfertaEspecialidad>(`${BASE}/servicios`, { method: "POST", body: payload });
}

export function actualizarServicio(
  servicioId: number,
  payload: ServicioUpdate,
): Promise<OfertaEspecialidad> {
  return apiFetch<OfertaEspecialidad>(`${BASE}/servicios/${servicioId}`, {
    method: "PATCH",
    body: payload,
  });
}

/** Estado operativo derivado — lo que le falta a la especialidad para agendar. */
export type EstadoOferta = "operativa" | "sin_precio" | "sin_profesional" | "no_ofrecida";

export function estadoDe(o: OfertaEspecialidad): EstadoOferta {
  if (!o.activa) return "no_ofrecida";
  const conProfesionalActivo = o.profesionales.some((p) => p.activo);
  if (!conProfesionalActivo) return "sin_profesional";
  if (o.precio == null) return "sin_precio";
  return "operativa";
}
